import { useEffect, useState } from "react";

/**
 * FlipDigit — 3D flip card that animates whenever the number changes.
 * Shows 2-digit zero-padded value.
 */
export default function FlipDigit({ value, label }) {
  const [current, setCurrent] = useState(value);
  const [next, setNext] = useState(value);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (value === current) return;
    setNext(value);
    setFlipping(true);
    const t = setTimeout(() => {
      setCurrent(value);
      setFlipping(false);
    }, 480);
    return () => clearTimeout(t);
  }, [value, current]);

  const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

  return (
    <div className="flex flex-col items-center gap-2 select-none" data-testid={`flip-${label}`}>
      <div
        className="relative w-[84px] h-[104px] lg:w-[104px] lg:h-[128px]"
        style={{ perspective: "700px" }}
      >
        {/* Static back (shows the `next` value) */}
        <div className="absolute inset-0 bg-[#1C1917] border border-[#292524] grid place-items-center overflow-hidden">
          <span className="font-display font-black tabular-nums text-[56px] lg:text-[72px] leading-none text-[#FFF7ED]">
            {pad(next)}
          </span>
        </div>
        {/* Flipping top half (shows the `current` value flipping down) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div
            className="absolute inset-0 origin-bottom"
            style={{
              transformOrigin: "50% 100%",
              transform: flipping ? "rotateX(-180deg)" : "rotateX(0deg)",
              transition: "transform 0.48s cubic-bezier(0.77, 0, 0.175, 1)",
              height: "50%",
              overflow: "hidden",
              background: "#0C0A09",
              borderTop: "1px solid #292524",
              borderLeft: "1px solid #292524",
              borderRight: "1px solid #292524",
            }}
          >
            <span
              className="absolute left-0 right-0 top-0 font-display font-black tabular-nums text-[56px] lg:text-[72px] leading-none text-[#FFF7ED] text-center"
              style={{ transform: "translateY(0%)" }}
            >
              {pad(current)}
            </span>
          </div>
        </div>
        {/* Center hinge */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-black/70" />
      </div>
      <span className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
        {label}
      </span>
    </div>
  );
}
